import { useEffect } from 'react';
import AnnouncementBar from '@/pages/home/components/AnnouncementBar';
import Navbar from '@/pages/home/components/Navbar';
import Hero from '@/pages/home/components/Hero';
import Genres from '@/pages/home/components/Genres';
import Service from '@/pages/home/components/Service';
import Regions from '@/pages/home/components/Regions';
import Company from '@/pages/home/components/Company';
import Faq from '@/pages/home/components/Faq';
import Contact from '@/pages/home/components/Contact';
import Footer from '@/pages/home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#business`,
  name: '買取専門店 天神屋',
  alternateName: '天神屋',
  description: '福岡の買取専門店「天神屋」は福岡・九州全域に出張買取・遺品整理サービスを提供。貴金属・ブランド品・カメラ・骨董品など12ジャンル対応。出張費・査定費無料。',
  url: SITE_URL,
  telephone: '0120-676-337',
  email: 'info@botan-buy.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '港1-2-18-1712',
    addressLocality: '福岡市中央区',
    addressRegion: '福岡県',
    postalCode: '810-0075',
    addressCountry: 'JP',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 33.5902,
    longitude: 130.3976,
  },
  areaServed: [
    { '@type': 'State', name: '福岡県' },
    { '@type': 'State', name: '佐賀県' },
    { '@type': 'State', name: '長崎県' },
    { '@type': 'State', name: '熊本県' },
    { '@type': 'State', name: '大分県' },
    { '@type': 'State', name: '宮崎県' },
    { '@type': 'State', name: '鹿児島県' },
    { '@type': 'State', name: '沖縄県' },
  ],
  priceRange: '無料査定',
  currenciesAccepted: 'JPY',
  paymentAccepted: '現金, 銀行振込',
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '09:00',
      closes: '19:00',
    },
  ],
  hasCredential: {
    '@type': 'EducationalOccupationalCredential',
    name: '古物商許可',
    credentialCategory: '古物商許可',
    recognizedBy: {
      '@type': 'Organization',
      name: '福岡県公安委員会',
    },
    identifier: '901012610013',
  },
  sameAs: ['https://lin.ee/OGQq9z6'],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '出張費・査定費はかかりますか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'いいえ、完全無料です。出張費・査定費・キャンセル料はすべて0円です。査定後にご納得いただけなかった場合も、一切費用はかかりません。お気軽にご依頼ください。',
      },
    },
    {
      '@type': 'Question',
      name: '査定後に断ることはできますか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'もちろんです。査定額にご納得いただけない場合は、その場でお断りいただけます。無理な買取交渉は一切行いませんのでご安心ください。',
      },
    },
    {
      '@type': 'Question',
      name: '対応エリアはどこですか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '福岡・九州全域を中心に対応しております。中国・四国・近畿エリアにも出張いたします。遠方の場合もまずはお気軽にご相談ください。',
      },
    },
    {
      '@type': 'Question',
      name: '遺品整理にも対応していますか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'はい、対応しております。故人のお品物を丁寧に査定し、ご遺族の気持ちに寄り添った対応を心がけております。生前整理のご相談も承っております。',
      },
    },
    {
      '@type': 'Question',
      name: '買取できないものはありますか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '状態や種類によってはお断りする場合がございます。また、偽造品・盗品などは法律により買取できません。事前にお電話やLINEでご相談いただくとスムーズです。',
      },
    },
    {
      '@type': 'Question',
      name: '支払いはいつ、どのような方法ですか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '原則として査定当日に現金でお支払いいたします。ご希望の場合は銀行振込にも対応しております。即日現金払いが基本ですのでご安心ください。',
      },
    },
    {
      '@type': 'Question',
      name: '複数の品物をまとめて査定してもらえますか？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'はい、何点でも対応可能です。ブランド品・貴金属・家電など複数ジャンルが混在していても一度にまとめて査定いたします。大量のお品物も歓迎です。',
      },
    },
    {
      '@type': 'Question',
      name: '予約から当日までの流れを教えてください。',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'お電話・WEBフォーム・LINEのいずれかでご連絡ください。ご希望の日時を調整後、スタッフがご自宅へ伺います。査定はその場で行い、ご納得いただければ即日現金でお支払いします。',
      },
    },
  ],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/#webpage`,
  url: SITE_URL,
  name: '買取専門店 天神屋｜買取専門店 出張買取・遺品整理 福岡・九州全域対応',
  description: '福岡の買取専門店「天神屋」は福岡・九州全域に出張買取・遺品整理サービスを提供。貴金属・ブランド品・カメラ・骨董品など12ジャンル対応。出張費・査定費無料。古物商許可(福岡県公安委員会 901012610013)取得済み。',
  inLanguage: 'ja',
  isPartOf: {
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: '買取専門店 天神屋',
    inLanguage: 'ja',
  },
  about: { '@id': `${SITE_URL}/#business` },
  dateModified: '2026-04-02',
};

const contactPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  '@id': `${SITE_URL}/#contact`,
  url: `${SITE_URL}/#contact`,
  name: 'お問い合わせ・買取予約｜天神屋 買取専門店',
  description: '天神屋の無料査定お申し込みページ。出張費・査定費無料。電話・LINE・WEBフォームでお気軽にご連絡ください。24時間以内にご連絡いたします。',
  inLanguage: 'ja',
  isPartOf: {
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: '買取専門店 天神屋',
  },
  about: { '@id': `${SITE_URL}/#business` },
  mainEntity: {
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#business`,
    name: '買取専門店 天神屋',
    telephone: '0120-676-337',
    email: 'info@botan-buy.com',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '0120-676-337',
        contactType: 'customer service',
        areaServed: 'JP',
        availableLanguage: 'Japanese',
        contactOption: 'TollFree',
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          opens: '09:00',
          closes: '19:00',
        },
      },
      {
        '@type': 'ContactPoint',
        email: 'info@botan-buy.com',
        contactType: 'customer service',
        areaServed: 'JP',
        availableLanguage: 'Japanese',
      },
    ],
  },
  dateModified: '2026-04-02',
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'ホーム',
      item: SITE_URL,
    },
  ],
};

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${SITE_URL}/#service`,
  name: '出張買取サービス',
  serviceType: '出張買取・遺品整理',
  description: '福岡・九州全域に出張し、貴金属・ブランド品・カメラ・骨董品など12ジャンルを高価買取。出張費・査定費・キャンセル料すべて無料。',
  provider: {
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#business`,
    name: '買取専門店 天神屋',
  },
  areaServed: [
    { '@type': 'State', name: '福岡県' },
    { '@type': 'State', name: '佐賀県' },
    { '@type': 'State', name: '長崎県' },
    { '@type': 'State', name: '熊本県' },
    { '@type': 'State', name: '大分県' },
    { '@type': 'State', name: '宮崎県' },
    { '@type': 'State', name: '鹿児島県' },
    { '@type': 'State', name: '沖縄県' },
  ],
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'JPY',
    description: '出張費・査定費・キャンセル料すべて無料',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: '買取ジャンル一覧',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '貴金属・宝石買取' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'ブランド品買取' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'カメラ・レンズ買取' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '骨董品・美術品買取' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '遺品整理・生前整理' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '切手・コイン買取' } },
    ],
  },
};

export default function Home() {
  useEffect(() => {
    const schemas = [localBusinessSchema, faqSchema, webPageSchema, contactPageSchema, breadcrumbSchema, serviceSchema];
    const scriptIds = ['schema-local-business', 'schema-faq', 'schema-webpage', 'schema-contact', 'schema-breadcrumb', 'schema-service'];

    schemas.forEach((schema, i) => {
      const existing = document.getElementById(scriptIds[i]);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.id = scriptIds[i];
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      scriptIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen">
      <AnnouncementBar />
      <Navbar />
      <Hero />
      <Genres />
      <Service />
      <Regions />
      <Company />
      <Faq />
      <Contact />
      <Footer />
    </div>
  );
}
